import React, { useEffect, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

// ui
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Search } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

// api and types
import {
  FacilitiesData,
  Membership,
} from "@/types/system-setting/membership";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";

import { useFormContext } from "react-hook-form";
import { Info } from "lucide-react";
import { useGetFacilitiesQuery } from "@/services/facilityApis";
import { Facility } from "@/types/facility";

interface TranformFacilitiesData {
  facility_id: number;
  total_credits: number;
  credits: number;
  credit_type?: string;
  count: number;
  validity: {
    type: string | undefined;
    duration: number | undefined;
  };
}

interface PayloadData {
  facility_id: number;
  credit_type: string;
  total_credits: number;
  validity: {
    type: string;
    duration: number;
  };
}

const findIndex = (id: number, array: Facility[]) => {
  return array?.findIndex((item) => item.id === id);
};

const validityOptions = [
  { value: "1_month", label: "1 Month" },
  { value: "3_month", label: "3 Months" },
  { value: "6_month", label: "6 Months" },
  { value: "12_month", label: "12 Months" },
];

export default function FacilityTableView({
  setFacilities,
}: {
  setFacilities: any;
}) {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { getValues } = useFormContext<Membership>();
  const {
    data: facilitiesData,
    isLoading,
    refetch,
  } = useGetFacilitiesQuery({ orgId: orgId, query: "status=active&is_add_on=false" });
  const [rowSelection, setRowSelection] = useState<Record<number, boolean>>({});
  const [showWarning, setShowWarning] = useState(false)
  const facilitiestableData = React.useMemo(() => {
    return Array.isArray(facilitiesData?.data) ? facilitiesData?.data : [];
  }, [facilitiesData]);

  const { toast } = useToast();
  const [transformedFacilities, setTransformedFacilities] = useState<
    TranformFacilitiesData[] | undefined
  >([]);
  const [payload] = useState<FacilitiesData[]>(
    getValues("facilities") ? getValues("facilities") : []
  );

  useEffect(() => {
    const data = facilitiesData?.data.map((item) => ({
      facility_id: item.id,
      count: 1,
      credits: item.min_limit,
      credit_type: "amount",
      total_credits: 0,
      validity: {
        type: undefined,
        duration: undefined,
      },
    }));


    if (payload.length > 0) {
      const newRowSelection: Record<number, boolean> = {};
      payload.forEach((item) => {
        const index = findIndex(
          item.facility_id,
          facilitiesData?.data as Facility[]
        );
        if (index !== -1) {
          newRowSelection[index] = true;
        }
      });

      const result =
        data?.map((item) => {
          const updatedItem = payload.find(
            (payloadItem) => payloadItem.facility_id === item.facility_id
          );

          if (updatedItem) {
            const count = (updatedItem.total_credits as number) / Number(item.credits);
            return {
              ...item,
              ...updatedItem,
              count: count,
              credits: item.credits,
            };
          }

          return item;
        }) || [];

      setRowSelection(newRowSelection);
      setTransformedFacilities(result as TranformFacilitiesData[]);
    } else {
      setTransformedFacilities(data as TranformFacilitiesData[]);
    }
  }, [facilitiesData, payload]);


  const handleChangeRowInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    id: number,
    key:
      | keyof TranformFacilitiesData["validity"],
    facilityKey: keyof Pick<TranformFacilitiesData, "validity">
  ) => {
    const { value } = e.target;

    setTransformedFacilities((prevCredits) =>
      prevCredits?.map((credit) =>
        credit.facility_id === id
          ? {
            ...credit,
            [facilityKey]: {
              ...credit[facilityKey],
              [key]: Number(value),
            },
          }
          : credit
      )
    );
  };

  const handleChangeRowSelect = (
    value: string,
    facilityKey: keyof Pick<TranformFacilitiesData, "validity">,
    id: number,
  ) => {
    setTransformedFacilities((prevCredits) =>
      prevCredits?.map((credit) => {
        if (credit.facility_id === id) {
          if (facilityKey === "validity") {
            // Set validity based on selected value
            let duration = 0;
            let type = "";

            if (value === "contract_duration") {
              type = "contract_duration";
              duration = 0;
            } else {
              // Split the value by underscore
              const [dur, typ] = value.split("_");
              duration = Number(dur);
              type = typ; // This will be "month"
            }

            return {
              ...credit,
              validity: {
                type: type,
                duration: duration,
              },
            };
          }
        }
        return credit;
      })
    );
  };

  const updateCredit = (id?: number, key?: string) => {
    if (!id || !key) return;

    setTransformedFacilities((prevCredits) => {
      return prevCredits?.map((credit) => {
        if (credit.facility_id === id) {
          if (key === "decrement") {
            if (credit.count === 1) {
              toast({
                variant: "destructive",
                title: "Minimum credits reached.",
              });
              return credit;
            }
            const updatedCount = credit.count - 1;
            const updatedTotalCredits = updatedCount * credit.credits;

            return {
              ...credit,
              count: updatedCount,
              total_credits: updatedTotalCredits,
            };
          } else if (key === "increment") {
            const updatedCount = credit.count + 1;
            const updatedTotalCredits = updatedCount * credit.credits;

            return {
              ...credit,
              count: updatedCount,
              total_credits: updatedTotalCredits,
            };
          }
        }
        return credit;
      });
    });
  };

  useEffect(() => {
    if (transformedFacilities && transformedFacilities?.length > 0) {
      const selectedIndexes = Object.keys(rowSelection).filter(
        (key) => rowSelection[Number(key)]
      );

      const selectedCredits = transformedFacilities
        ?.filter((_, index) => selectedIndexes.includes(index.toString()))
        .map(
          ({
            facility_id,
            total_credits,
            validity,
            credit_type,
          }) => ({
            facility_id,
            total_credits,
            credit_type,
            validity:
              credit_type === "unlimited"
                ? {
                  type: "",
                  duration: 0,
                }
                : validity,
          })
        )
        .filter((item): item is PayloadData => item !== null);

      setShowWarning(false);

      setFacilities(selectedCredits);
    }
  }, [rowSelection, transformedFacilities]);

  const columns: ColumnDef<Facility>[] = [
    {
      id: "select",
      maxSize: 50,
      size: 50,
      minSize: 50,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: any) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px] "
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: any) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      header: ({ table }) => <p>Name</p>,
      cell: ({ row }) => {
        return <p className="text-nowrap capitalize">{row.original.name}</p>;
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "credit_type",
      header: () => <p className="text-nowrap">Credit Type</p>,
      cell: ({ row }) =>
        row.getIsSelected() ? (
          <span className="text-sm text-gray-700">Amount</span>
        ) : null,
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "credits_include",
      header: "Credits Included",
      cell: ({ row }) => {
        const id = row.original.id;
        const Credit = transformedFacilities?.find(
          (data) => data?.facility_id == id
        );

        return row.getIsSelected() && Credit?.credit_type === "amount" ? (
          <div className="flex items-center gap-2 justify-between !max-w-4xl">
            <span className="text-xs text-nowrap w-full max-w-40">
              (Min. required credit: {row?.original?.min_limit})
            </span>
            <div className="flex h-8 items-center justify-between gap-2 w-full max-w-36 bg-white border border-primary rounded-lg p-2">
              <button
                onClick={() => updateCredit(id, "decrement")}
                className="text-black bg-white border border-primary rounded-lg h-6 px-1"
              >
                <i className="fa fa-minus font-semibold"></i>
              </button>
              <div className="text-black">{Credit?.total_credits}</div>
              <button
                onClick={() => updateCredit(id, "increment")}
                className="text-white bg-primary rounded-lg h-6 px-1"
              >
                <i className="fa fa-plus font-semibold"></i>
              </button>
            </div>
          </div>
        ) : null;
      },
    },
    {
      id: "validity",
      header: "Validity",
      maxSize: 100,
      cell: ({ row }) => {
        const id = row.original.id;
        const creditData = transformedFacilities?.find(
          (credit) => credit.facility_id === id
        );

        const validityOptionsForFrequency = validityOptions;

        const validityValue = creditData?.validity?.type == "contract_duration" ? creditData?.validity?.type : creditData?.validity?.type && `${creditData?.validity?.duration}_${creditData?.validity?.type}`
        return row.getIsSelected() && creditData?.credit_type === "amount" ? (
          <div className="flex items-center gap-2">
            <Select
              onValueChange={(value) => {
                handleChangeRowSelect(value, "validity", id as number);
              }}
              value={validityValue || undefined} // Ensure this reflects the current validity type
            >
              <SelectTrigger className="bg-white h-8">
                <SelectValue placeholder="Select validity" />
              </SelectTrigger>
              <SelectContent>
                {validityOptionsForFrequency.map((option: any) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : null;
      },
    }
  ];

  const table = useReactTable({
    data: facilitiestableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    state: {
      rowSelection,
    },
  });

  return (
    <div className="w-full space-y-4">
      {showWarning && (
        <div className="flex gap-2 items-center bg-yellow-100 p-4 rounded text-yellow-800 text-sm w-fit">
          <span>
            <Info className="text-yellow-800" />
          </span>
          <span>
            Inconsistent Frequency and Validity may cause unexpected credit expiration or renewal, Please recheck.
          </span>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-1 items-center">
          <div className="flex flex-col sm:flex-row flex-1 items-start sm:items-center gap-2 sm:gap-4">
            <h1 className="font-semibold text-[#2D374] text-xl">
              Facility details
            </h1>



            <div className="flex items-center w-full sm:w-auto gap-2 px-3 py-1 rounded-md border text-sm border-gray-300 focus-within:border-primary focus-within:ring-[1] ring-primary">
              <Search className="w-[14px] h-[14px] text-gray-400 m-auto " />
              <input
                type="text"
                placeholder="Search by Name"
                value={
                  (table.getColumn("name")?.getFilterValue() as string) ?? ""
                }
                onChange={(event) =>
                  table.getColumn("name")?.setFilterValue(event.target.value)
                }
                className="h-7  outline-none"
                maxLength={50}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="rounded-none  ">
        <ScrollArea className="w-full relative" >
          <ScrollBar
            orientation="horizontal"
            className="relative z-30 cursor-grab h-2"
          />
          {/* <Table className="w-full overflow-x-scroll"> */}
          <Table className="w-full custom-scrollbar sm:max-h-[40vh] lg:max-h-[50vh] xl:max-h-[60vh] 2xl:max-h-[65vh]  ">
            <TableHeader className="bg-gray-100 sticky top-0 z-20">
              {table?.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <TableHead
                        key={header.id}
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody className="">
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center "
                  >
                    <div className="flex space-x-2 justify-center items-center bg-white ">
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                      <div className="size-3 bg-black rounded-full animate-bounce"></div>
                    </div>
                  </TableCell>
                </TableRow>
              ) : table.getRowModel().rows.length ? (
                table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && "selected"}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : facilitiestableData.length > 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No facility found.
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={columns.length}
                    className="h-24 text-center"
                  >
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </ScrollArea>
      </div>
    </div>
  );
}
